import { css } from '@emotion/react'
import { Component } from 'react'
import { observer, inject } from 'mobx-react'
import { types, flow, getEnv } from 'mobx-state-tree'
import _ from 'lodash'
import { withRouterProps } from './utils'
import { API } from './api'
import { Logout } from './views'
import { Loading, LoadingMessage } from './components'

const { model, enumeration } = types

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

const authCheck = async state => {
  let checkStatus = false
  let retryCount = 0

  while (checkStatus === false) {
    const response = await API.get('authTracker')
    checkStatus = _.get(response.data, 'complete', false)

    if (!checkStatus) {
      retryCount = retryCount + 1
      await sleep(1100)
    }

    const authTrackerStatus = _.get(response.data, 'status', 'loading')
    if (authTrackerStatus === 'ERROR') {
      const authTrackerErrorMessage = _.get(response.data, 'errorDetails.message', '')
      if (authTrackerErrorMessage.length > 0) {
        state.setErrorMessage(authTrackerErrorMessage)
        throw new Error(authTrackerErrorMessage)
      } else {
        throw new Error('Error during auth process')
      }
    }

    if (retryCount > 30) {
      throw new Error('Retry limit exceeded (30)')
    }
  }
}

const State = model({
  status: enumeration(['loading', 'done', 'error']),
  tokenExists: false,
  errorMessage: '',
}).actions(self => ({
  afterCreate: flow(function* () {
    self.status = 'loading'
    const { store, navigate, location } = getEnv(self)
    const params = new URLSearchParams(location?.search)
    const { auth, getConfig, getFeatures, getAccount } = store

    try {
      if (params.get('sso') && params.get('signature') && params.get('recordKeeper')) {
        self.tokenExists = true

        yield auth.authenticate(
          params.get('sso'),
          params.get('signature'),
          params.get('recordKeeper')
        )
      }

      yield authCheck(self)

      yield Promise.all([getConfig(), getFeatures(), getAccount()])

      const { config } = store

      // Landing page for Discontinue or Upgrade to Managed Account
      if (config.template.landingPage === 'productSelect') {
        navigate('/product-select', { replace: true })
        auth.authorize()
        self.status = 'done'
        return
      }

      // Standard Application Entry Point
      if (!config.onBoarded) {
        navigate('/welcome', { replace: true })
      }

      if (config.onBoarded && config.requireToAcceptTerms) {
        navigate('/welcome/reaccept-terms', { replace: true })
      }

      auth.authorize()
      self.status = 'done'
    } catch (err) {
      console.error(err)
      self.status = 'error'
    }
  }),
  setErrorMessage(errorMessage) {
    self.errorMessage = errorMessage
  },
}))

class AuthCheck extends Component {
  constructor(props) {
    super(props)
    const { store, navigate, location } = props
    this.state = State.create({ status: 'loading' }, { store, navigate, location })
  }

  render() {
    if (this.state.status === 'error') {
      return <Logout errorMessage={this.state.errorMessage} />
    }

    return (
      <div
        css={css`
          padding: 20px;
        `}>
        <LoadingMessage />
        <Loading />
      </div>
    )
  }
}

export default withRouterProps(inject('store')(observer(AuthCheck)))
